import { MenuItem } from "@/app/ui/navigation/types";
import { EnumFeatureFlag } from "@/app/services/generated/enums/enumFeatureFlag.generated";
import { RouteBuilder } from "@/app/ui/navigation/routeBuilder";
import type Guid from "@/app/types/common/guid";
import { keys } from "@/plugins/i18n";

const builder = new RouteBuilder("Gallery_");

export const galleryRoutes = {
  typography: builder.route({
    url: "/gallery/typography",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Typography.vue"
      ),
  }),
  controls: builder.route({
    url: "/gallery/controls",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Controls.vue"
      ),
  }),
  singleControls: builder.route({
    url: "/gallery/singleControls",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/SingleControls.vue"
      ),
  }),
  production: builder.route({
    url: "/gallery/production",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Production.vue"
      ),
  }),
  grids: builder.route({
    url: "/gallery/grids",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Grids.vue"
      ),
  }),
  largeGrid: builder.route({
    url: "/gallery/largegrid",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/LargeGrid.vue"
      ),
  }),
  geocode: builder.route({
    url: "/gallery/geocode",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Geocode.vue"
      ),
  }),
  maps: builder.route({
    url: "/gallery/maps",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Maps.vue"
      ),
  }),
  pageLayout: builder.route({
    url: "/gallery/page_layout",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/PageLayout.vue"
      ),
  }),
  validation: builder.route({
    url: "/gallery/validation",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Validation.vue"
      ),
  }),
  tableau: builder.route({
    url: "/gallery/tableau/reporting/RevenueMap",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/reporting/embeddedtableau/EmbeddedTableauReport.vue"
      ),
    componentProps: (route) => ({
      workbook: "Reporting",
      view: "RevenueMap",
    }),
  }),
  timeline: builder.route({
    url: "/gallery/timeline",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Timeline.vue"
      ),
  }),
  pipeline: builder.route({
    url: "/gallery/pipeline",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/LeadsPipelineGallery.vue"
      ),
  }),
  errorTests: builder.route({
    url: "/gallery/error_testing",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/ErrorTests.vue"
      ),
  }),
  platformSingleList: builder.route({
    url: "/gallery/platform",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/platformSingleList"
      ),
  }),
  editPage: builder.routeWithParams(
    {
      url: "/gallery/editpage/:id/:mode",
      view: (): Promise<any> =>
        import(
          /* webpackChunkName: "gallery" */ "@/components/pages/gallery/platformEditPage"
        ),
      componentProps: (route) => {
        return {
          id: route.params.id,
          editing:
            route.params.id &&
            route.params.mode.toLowerCase().trim() === "edit",
        };
      },
    },
    (opts: { id: Guid; editing: boolean }) => ({
      id: opts.id.value,
      mode: opts.editing ? "edit" : "view",
    })
  ),
  editPageList: builder.route({
    url: "/gallery/editpage",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/platformSingleList"
      ),
  }),
  editPageCreate: builder.route({
    url: "/gallery/editpage/create",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/platformEditPage"
      ),
    componentProps: (route) => ({
      editing: true,
    }),
  }),
  buttonBarPlayground: builder.route({
    url: "/gallery/buttonbar",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/ButtonBarPlayground.vue"
      ),
  }),
  platformFlowForm: builder.route({
    url: "/gallery/flowform",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/platformFlowForm"
      ),
  }),
  logging: builder.route({
    url: "/gallery/logging",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/Logging.vue"
      ),
  }),
  forms: builder.route({
    url: "/gallery/forms",
    view: (): Promise<any> =>
      import(
        /* webpackChunkName: "gallery" */ "@/components/pages/gallery/forms/GalleryFormParent.vue"
      ),
  }),
};

export const buildGalleryMenuItems: () => MenuItem[] = () => [
  MenuItem.forBranch({
    translationKey: keys.navigation_gallery_group.value,
    icon: "web",
    requiredFeatureFlag: EnumFeatureFlag.showGallery,
    subMenus: [
      MenuItem.forRoute("Typography", galleryRoutes.typography),
      MenuItem.forRoute("Controls", galleryRoutes.controls),
      MenuItem.forRoute("Single Controls", galleryRoutes.singleControls),
      MenuItem.forRoute("Production", galleryRoutes.production),
      MenuItem.forRoute("Grids", galleryRoutes.grids),
      MenuItem.forRoute("Large Grid", galleryRoutes.largeGrid),
      MenuItem.forRoute("Geocode", galleryRoutes.geocode),
      MenuItem.forRoute("Maps", galleryRoutes.maps),
      MenuItem.forRoute("Page Layout", galleryRoutes.pageLayout),
      MenuItem.forRoute("Validation", galleryRoutes.validation),
      MenuItem.forRoute("Tableau Embedded", galleryRoutes.tableau),
      MenuItem.forRoute("Timeline", galleryRoutes.timeline),
      MenuItem.forRoute("Pipeline", galleryRoutes.pipeline),
      MenuItem.forRoute("Error Tests", galleryRoutes.errorTests),
      MenuItem.forRoute("Platform List", galleryRoutes.platformSingleList),
      MenuItem.forRoute("Edit Page", galleryRoutes.editPage),
      MenuItem.forRoute("Edit Page List", galleryRoutes.editPageList),
      MenuItem.forRoute("Edit Page Create", galleryRoutes.editPageCreate),
      MenuItem.forRoute("Button Bar", galleryRoutes.buttonBarPlayground),
      MenuItem.forRoute("Platform Flow Form", galleryRoutes.platformFlowForm),
      MenuItem.forRoute("Logging", galleryRoutes.logging),
      MenuItem.forRoute("Forms", galleryRoutes.forms),
    ],
  }),
];

//export const buildGalleryMenuItems: () => MenuItem[] = () => [];

/*export const galleryMenu = {
  root: "navigation_gallery_group",
  typography: "typography",
  controls: "controls",
  singleControls: "singleControls",
  production: "production_gallery",
  grids: "grids",
  viewGrids: (): Location => ({
    name: galleryMenu.grids,
  }),
  largeGrid: "large_grid",
  geocode: "geocode",
  maps: "maps",
  pageLayout: "page_layout",
  async: "async",
  validation: "validation",
  timeline: "timeline",
  pipeline: "pipeline",
  dataProviders: "data_providers",
  tableauEmbedded: "tableau_embedded",
  errorTests: "error_tests",
  platformSingleList: "platformSingleList",
  editPage: "edit_page",
  editPageList: "edit_page_list",
  editPageCreate: "edit_page_create",
  galleryCreateEntity: (): Location => ({
    name: galleryMenu.editPageCreate,
  }),
  galleryEditEntity: (id: Guid, isEditing: boolean): Location => ({
    name: galleryMenu.editPage,
    params: {
      id: id.toString(),
      mode: isEditing ? "edit" : "view",
    },
  }),
  galleryListEntity: (): Location => ({
    name: galleryMenu.editPageList,
  }),
  buttonBarPlayground: "buttonBarPlayground",
  platformFlowForm: "platformFlowForm",
  logging: "logging",
  forms: "forms",
};
*/
